import React, { memo, useCallback, useState } from 'react'
import { Control, FieldErrors } from 'react-hook-form/dist/types'
import { ControlledSelect, ControlledTextInput } from 'transfix-form/components'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { theme } from 'transfix-ui/theme'
import { getErrorMessage } from 'apps/edi-tools/utils'
import { useAs2ConnectionConfigsQuery } from '../AS2ConnectionConfig/graphql/AS2ConnectionConfigs'

interface IAs2ConnectionConfigGridProps {
  includeFilenameGlob: boolean
  control?: Control
  errors?: FieldErrors
}

const useAs2ConnectionConfigGridStyles = makeStyles({
  container: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  text: {
    whiteSpace: 'pre-line',
  },
  gridItem: {
    display: 'flex',
    alignItems: 'center',
  },
})

export const As2ConnectionConfigGrid = memo<IAs2ConnectionConfigGridProps>(
  ({ control, errors, includeFilenameGlob }) => {
    const classes = useAs2ConnectionConfigGridStyles()
    const [as2ConnectionConfigId, setas2ConnectionConfigId] = useState<number>()

    const { data } = useAs2ConnectionConfigsQuery({
      notifyOnNetworkStatusChange: true,
    })

    const as2ConnectionConfigChangeHandler = useCallback(({ target }) => {
      const val = target.value

      setas2ConnectionConfigId(val)
    }, [])

    const { as2ConnectionConfigs } = data || {}
    const { nodes } = as2ConnectionConfigs || {}

    if (!errors) {
      return null
    }

    const as2ConnectionConfigsOptions = nodes
      ? nodes.map(as2ConnectionConfig => {
          return as2ConnectionConfig
            ? {
                label: `${as2ConnectionConfig.identifier}, ${as2ConnectionConfig.transfixIdentifier}`,
                value: as2ConnectionConfig.id,
              }
            : { label: '', value: '' }
        })
      : []

    return (
      <div>
        <Box mt={2} mb={2}>
          <Typography variant='h3'>AS2 Connection Details</Typography>
        </Box>
        <Grid
          container
          justifyContent='flex-start'
          direction='column'
          spacing={1}
          className={classes.container}>
          <Grid item className={classes.gridItem}>
            <ControlledSelect
              control={control}
              name='as2ConnectionConfigId'
              onChange={as2ConnectionConfigChangeHandler}
              SelectProps={{
                id: 'as2ConnectionConfigId',
                dataTest: 'as2-connection-config-select',
                label: 'AS2 Connection Config',
                options: as2ConnectionConfigsOptions,
                value: as2ConnectionConfigId,
                error: !!getErrorMessage(errors, 'as2ConnectionConfigId'),
                validationMessage: getErrorMessage(errors, 'as2ConnectionConfigId'),
              }}
            />
          </Grid>
          {includeFilenameGlob && (
            <Grid item className={classes.gridItem}>
              <ControlledTextInput
                control={control}
                name='filenameGlob'
                TextInputProps={{
                  label: 'Filename GLOB',
                  dataTest: 'filename-glob-field',
                  error: !!getErrorMessage(errors, 'filenameGlob'),
                  validationMessage: getErrorMessage(errors, 'filenameGlob'),
                }}
              />
            </Grid>
          )}
        </Grid>
      </div>
    )
  }
)
